import React, { useState, useEffect } from 'react';
import { Wrench, Check, Info, AlertTriangle } from 'lucide-react';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

/**
 * Affiche les supports muraux MountCam compatibles avec la caméra courante.
 * Compatibilité :
 * - HLVF (sauf BulletCam HLVF) → MountCam A2 / B2 (avec adaptateur motorisé)
 * - IR / HL standard → MountCam A1 / B1
 * - BulletCam HLVF → aucun support compatible (note Ajax)
 */
const CameraMountOptions = ({ onMountChange, productName }) => {
  const [allMounts, setAllMounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMountId, setSelectedMountId] = useState('none');

  useEffect(() => {
    const fetchMounts = async () => {
      try {
        const response = await fetch(`${API}/products?category=video_basic`);
        if (response.ok) {
          const data = await response.json();
          const mounts = data.filter(p => p.subcategory === 'mount');
          setAllMounts(mounts);
        }
      } catch (error) {
        console.error('Failed to fetch mounts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMounts();
  }, []);

  // Détermine la compatibilité selon le nom du produit caméra
  const getCompatibleMounts = () => {
    if (!productName) return [];
    const name = productName.toLowerCase();
    const isHLVF = name.includes('hlvf');
    const isBullet = name.includes('bulletcam');

    // BulletCam HLVF : aucun support compatible
    if (isHLVF && isBullet) return [];

    if (isHLVF) {
      // TurretCam / DomeCam HLVF → A2, B2 (avec adaptateur motorisé)
      return allMounts.filter(m => /\bA2\b|\bB2\b/.test(m.name));
    }
    // IR / HL standard → A1, B1, JunctionBox
    return allMounts.filter(m => /\bA1\b|\bB1\b|JunctionBox/i.test(m.name));
  };

  const compatibleMounts = getCompatibleMounts();
  const isBulletHLVF = productName?.toLowerCase().includes('hlvf') &&
                       productName?.toLowerCase().includes('bulletcam');

  const handleSelect = (mountId) => {
    setSelectedMountId(mountId);
    if (mountId === 'none') {
      onMountChange && onMountChange(null, 0);
    } else {
      const mount = compatibleMounts.find(m => m.product_id === mountId);
      if (mount) {
        onMountChange && onMountChange({
          product_id: mount.product_id,
          name: mount.name,
          price: mount.price,
          quantity: 1
        }, mount.price);
      }
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-8 bg-zinc-800 rounded w-1/3" />
        <div className="h-24 bg-zinc-800 rounded" />
      </div>
    );
  }

  // BulletCam HLVF : afficher une note explicative et ne pas proposer de support
  if (isBulletHLVF) {
    return (
      <div className="space-y-2" data-testid="camera-mount-options">
        <div className="flex items-center gap-2">
          <Wrench className="w-4 h-4 text-amber-400" />
          <h4 className="text-sm font-semibold text-white">Support mural</h4>
        </div>
        <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-white/70">
              La BulletCam HLVF n'est pas compatible avec les supports MountCam standards. Un support spécifique sera proposé prochainement par Ajax.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (compatibleMounts.length === 0) return null;

  return (
    <div className="space-y-4" data-testid="camera-mount-options">
      <div className="flex items-center gap-2">
        <Wrench className="w-4 h-4 text-amber-400" />
        <h4 className="text-sm font-semibold text-white">Support mural MountCam</h4>
        <span className="text-xs text-white/50">(optionnel)</span>
      </div>

      <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg mb-3">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-white/70">
            Un support mural Ajax MountCam est conçu spécifiquement pour votre caméra et permet un câblage caché propre. Choisissez la longueur selon l'angle souhaité.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {/* Option : pas de support */}
        <label
          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
            selectedMountId === 'none'
              ? 'bg-zinc-800/50 border-zinc-600'
              : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedMountId === 'none' ? 'border-amber-500 bg-amber-500' : 'border-zinc-600'
              }`}
            >
              {selectedMountId === 'none' && <Check className="w-3 h-3 text-white" />}
            </div>
            <div>
              <p className="text-sm font-medium text-white">Pas de support</p>
              <p className="text-xs text-white/50">J'utilise ma propre fixation</p>
            </div>
          </div>
          <span className="text-sm font-bold text-white/70">Inclus</span>
          <input
            type="radio"
            name="mount"
            value="none"
            checked={selectedMountId === 'none'}
            onChange={() => handleSelect('none')}
            className="sr-only"
          />
        </label>

        {compatibleMounts.map(mount => (
          <label
            key={mount.product_id}
            data-testid={`mount-option-${mount.product_id}`}
            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
              selectedMountId === mount.product_id
                ? 'bg-amber-500/10 border-amber-500/30'
                : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedMountId === mount.product_id
                    ? 'border-amber-500 bg-amber-500'
                    : 'border-zinc-600'
                }`}
              >
                {selectedMountId === mount.product_id && <Check className="w-3 h-3 text-white" />}
              </div>
              <div>
                <p className="text-sm font-medium text-white">{mount.name}</p>
                <p className="text-xs text-white/50">{mount.description}</p>
              </div>
            </div>
            <span className="text-sm font-bold text-amber-400">+{mount.price.toFixed(2)}€</span>
            <input
              type="radio"
              name="mount"
              value={mount.product_id}
              checked={selectedMountId === mount.product_id}
              onChange={() => handleSelect(mount.product_id)}
              className="sr-only"
            />
          </label>
        ))}
      </div>
    </div>
  );
};

export default CameraMountOptions;
